import { TicketCommunicationService } from './../../ticket/ticket-communication.service';
import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {DialogService} from "../../../services/core/dialog.service";
import {BackendApiService} from "../../../services/backend-api.service";
import {TicketDetailPopupComponent} from "./ticket-detail-popup/ticket-detail-popup.component";
import { ViewTicketDetailComponent } from '../../ticket/view-ticket-detail/view-ticket-detail.component';
import { AddEditTicketComponent } from '../../ticket/add-edit-ticket/add-edit-ticket.component';
import {debounceTime, distinctUntilChanged, Subject, Subscription, switchMap} from 'rxjs';
import { HelperService } from 'src/app/services/helper.service';
import { ExportPdfSharedComponent } from '../export-pdf/export-pdf.component';
import { environment } from 'src/environments/environment';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import {ToasterService} from "../../../services/core/toaster.service";
import {CommunicationServiceService} from "../../widgets/services/communication-service.service";

/**
 * Tag Interface for Tickets
 */
interface TicketTag {
  id?: number;
  tag_id?: number;
  name?: string;
  tag?: string;
  color?: string;
  tag_color?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Enhanced TicketViewModel Interface - Aligned with Change Order patterns
 */
interface TicketViewModel {
  // Direct from API
  id: number;
  code: string;
  name: string;
  description?: string;
  billable_label?: string;
  status_label: { name: string; color: string };
  priority_label: string;
  responsible_persons: any[];
  scopes: any[];
  reference: any;
  ticket_type: any;
  created_on: string;
  due_date: string;
  created_by: any;
  tags?: any[]; // Raw tags from API
  party_name?: string; // Responsible Party name

  // Client-side calculations - Enhanced
  approvalProgress: number;
  daysInStatus: number;
  ageInDays: number;
  isNew: boolean;
  isAging: boolean;
  isOverdue: boolean;
  priorityLevel: 'high' | 'medium' | 'low';
  statusClass: string;

  // Expandable sections state
  showInfo: boolean;
  showDetails: boolean;
  showActivity: boolean;

  // Display helpers
  scopeDisplayText: string;
  primaryScope: string;
  assigneeDisplayText: string;
  createdDaysAgo: string;
  formattedDueDate: string;
  truncatedDescription: string;
  hasDescription: boolean;

  // Smart indicators
  requiresAttention: boolean;
  hasMultipleAssignees: boolean;
  hasScopes: boolean;

  // Tag-related properties
  processedTags: TicketTag[];
  hasMultipleTags: boolean;
  primaryTag: TicketTag | null;
}

@Component({
    selector: 'app-module-tickets',
    templateUrl: './module-tickets.component.html',
    styleUrls: ['./module-tickets.component.scss'],
    standalone: false
})
export class ModuleTicketsComponent implements OnInit, OnChanges {

  @Input() module_id: any;
  @Input() record_id: any;
  @Input() customerID: any = '';
  @Input() vendorID: any = '';
  @Input() subcontractorID: any = '';
  @Input() wareHouseId:any=''
  @Input() customerModel: any = null; // Customer data for pre-selection
  @Input() isLocked: boolean = false;
  @Input() vendorModel: any = null; // Vendor data for pre-selection
  @Input() subcontractorModel: any = null; // Subcontractor data for pre-selection
  //@ts-ignore
  scrumDataSubscription: Subscription

  customerModuleID: any = 13;
  ticketList: TicketViewModel[] = [];
  search: string='';
  private searchInput = new Subject<string>();
  private readonly debounceTimeMs = 500; // Adjust debounce time as needed
  showLoadMoreOption: boolean = false;
  param = { per_page: 10, page: 1 };
  ticketMeta: any;
  
  // View modes - matching Change Order component
  viewMode: 'grid' | 'list' = 'list';
  
  // Loading states - matching Change Order component
  isLoading: boolean = false;
  isSearching: boolean = false;
  isLoadingMore: boolean = false;

  //Permissions
  @Input() createPermission = 'DONOTAPPLY';
  @Input() editPermission = 'DONOTAPPLY';
  @Input() deletePermission = 'DONOTAPPLY';
  @Input() changeStatusPermission = 'DONOTAPPLY';
  @Input() viewDetailPermission = 'DONOTAPPLY';
  @Input() exportPDFPermission = 'DONOTAPPLY';



  constructor(
    public dialogService: DialogService,
    public backendApiService: BackendApiService,
    public scrumCommunication: CommunicationServiceService,
    public helperService: HelperService,
    public dialog: NgDialogAnimationService,
    public toasterService: ToasterService,
    private ticketCommunicationService:TicketCommunicationService
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  /**
   * This component instance is reused across in-app navigation to a
   * DIFFERENT record (e.g. Bids list -> bid A's Tickets tab -> bid B's
   * Tickets tab without a full page reload) — record_id goes from one real
   * value straight to another. Without this, ngOnInit's one-time fetch means
   * the list keeps showing the previous record's stale tickets until the
   * page is refreshed.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const recordId = changes['record_id'];
    if (!recordId || recordId.firstChange) {
      return;
    }
    if (recordId.currentValue && recordId.currentValue !== recordId.previousValue) {
      this.param.page = 1;
      this.ticketList = [];
      this.getTickets();
    }
  }

  /**
   * Initialize the component with initial data load and search setup
   */
  private initializeComponent(): void {
    this.initializeViewMode();
    this.isLoading = true;
    this.getTickets();
    this.searching();
    this.initSubscription();
  }

  /**
   * Initialize view mode from localStorage
   */
  initializeViewMode(): void {
    const savedViewMode = localStorage.getItem('tickets-view-mode');
    if (savedViewMode === 'list' || savedViewMode === 'grid') {
      this.viewMode = savedViewMode;
    }
  }

  /**
   * Fetch tickets data with loading state management
   * @param isLoadMore - Whether this is a load more operation
   */
  getTickets(isLoadMore: boolean = false) {
    console.log(`module/tickets/${this.module_id}/${this.record_id}?customer_id=${this.customerID}&subcontractor_id=${this.subcontractorID}&vendor_id=${this.vendorID}`);

    // Set appropriate loading state
    if (isLoadMore) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.isSearching = !!this.search;
    }

    let url=`module/tickets/${this.module_id}/${this.record_id}?customer_id=${this.customerID}&subcontractor_id=${this.subcontractorID}&vendor_id=${this.vendorID}&search=${this.search}`
    this.backendApiService.getApi(url,this.param)
      .subscribe({
        next: (response: any) => {
          console.log('response',response);
          const data = response?.data;
          
          if (isLoadMore) {
            // Append new data for load more
            this.ticketList = [...this.ticketList, ...this.processTickets(data || [])];
          } else {
            // Replace data for new search or initial load
            this.ticketList = this.processTickets(data || []);
          }
          
          this.ticketMeta = response?.meta;
          this.showLoadMoreOption = this.helperService.checkLoadMoreOptionVisibility(
            isLoadMore, 
            this.ticketList, 
            this.ticketMeta, 
            this.param
          );
          
          // Reset loading states
          this.isLoading = false;
          this.isSearching = false;
          this.isLoadingMore = false;
        },
        error: (error) => {
          console.error('Error fetching tickets:', error);
          
          // Reset loading states on error
          this.isLoading = false;
          this.isSearching = false;
          this.isLoadingMore = false;
          
          // Reset list on error for non-load-more operations
          if (!isLoadMore) {
            this.ticketList = [];
          }
        }
      });
  }

  /**
   * Process tickets to enhance with calculated fields - matching Change Order pattern
   */
  processTickets(data: any[]): TicketViewModel[] {
    return data.map(ticket => {
      const approvalProgress = this.calculateTicketProgress(ticket);
      const ageInDays = this.calculateDaysFromCreation(ticket.created_on);
      const daysInStatus = this.calculateDaysInStatus(ticket.updated_on || ticket.created_on);
      const priorityLevel = this.getPriorityLevel(ticket.priority_label);

      // Process tags
      const processedTags = this.processTags(ticket.tags || []);

      return {
        ...ticket,
        // Enhanced calculations
        approvalProgress,
        daysInStatus,
        ageInDays,
        isNew: ageInDays <= 3,
        isAging: ageInDays > 30,
        isOverdue: this.checkIfOverdue(ticket.due_date),
        priorityLevel,
        statusClass: this.getStatusClassFromColor(ticket.status_label?.color),

        // Expandable sections - default states
        showInfo: false,
        showDetails: true,
        showActivity: false,

        // Display helpers
        scopeDisplayText: this.getScopeDisplayText(ticket.scopes),
        primaryScope: ticket.scopes?.[0]?.name || 'No scope',
        assigneeDisplayText: this.getAssigneeDisplayText(ticket.responsible_persons),
        createdDaysAgo: this.calculateDaysAgo(ticket.created_on),
        formattedDueDate: this.formatShortDate(ticket.due_date),
        truncatedDescription: this.getTruncatedDescription(ticket.description),
        hasDescription: !!(ticket.description && ticket.description.trim()),

        // Smart indicators
        requiresAttention: this.checkRequiresAttention(ticket),
        hasMultipleAssignees: ticket.responsible_persons?.length > 1,
        hasScopes: ticket.scopes?.length > 0,

        // Tag-related properties
        processedTags: processedTags,
        hasMultipleTags: processedTags.length > 1,
        primaryTag: processedTags.length > 0 ? processedTags[0] : null
      };
    });
  }
  initSubscription() {
    this.scrumDataSubscription = this.scrumCommunication.getScrumBoardData()
      .subscribe(response => {
        if(response) {
          this.getTickets();
        }
      })
  }
  openTicketCommentDetail(id: any) {
    this.dialogService.onOpenFromRightDialog(TicketDetailPopupComponent, {
      id: id,
      module_id: this.module_id,
    }, 710);

    this.dialogService.afterClose()?.subscribe((result: any) => {
      if (result) {
        this.getTickets();
        // this.router.navigate(['services/service/' + result?.data?.id]);
      }
    });
  }

  openTicketDetail(id: any) {
    this.dialogService.onOpenFromRightDialog(ViewTicketDetailComponent,{
      id:id,
      permissions:{
        createPermission: this.createPermission,
        editPermission: this.editPermission,
        deletePermission: this.deletePermission,
        changeStatusPermission: this.changeStatusPermission,
        exportPDFPermission: this.exportPDFPermission,
      }
    },1300);
    this.dialogService.afterClose()?.subscribe((result: any) => {
      if(result) {
        this.getTickets();
      }
    });
  }

  createTicket(): void {
    if (this.isLocked) {
      return;
    }
    console.log('this.moduleID', this.module_id);
    console.log('this.referenceID', this.record_id);
    // this.dialogService.onOpenFromRightDialog(CreateTicketComponent);
    this.dialogService.onOpenFromRightDialog(AddEditTicketComponent,{
      moduleID: this.module_id,
      referenceID: this.record_id,
      preSelectedCustomer: this.customerModel, // Pass customer data if available
      preSelectedVendor: this.vendorModel, // Pass vendor data if available
      preSelectedSubcontractor: this.subcontractorModel, // Pass subcontractor data if available
    },1300);
    this.dialogService.afterClose()?.subscribe((result: any) => {
      if(result) {
        this.getTickets();
      }
    });
  }
  onSearchTextChange() {
    this.searchInput.next(this.search);
  }

  searching() {
    // Get Updated Ticket List
    this.ticketCommunicationService.getTicketListUpdate().subscribe(response=>{
      if(response){
        this.getTickets()
      }
    })

    // Search Ticket List
    this.searchInput
      .pipe(
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged(),
        switchMap(async () => {
          this.getTickets();
        })
      )
      .subscribe(); // Subscribe to the observable chain
  }

  loadMoreData() {
    if (this.isLoadingMore) {
      return; // Prevent multiple simultaneous requests
    }

    this.showLoadMoreOption = this.helperService.checkLoadMoreOptionVisibility(
      true, 
      this.ticketList, 
      this.ticketMeta, 
      this.param
    );
    
    if (this.showLoadMoreOption) {
      this.param.page += 1;
      this.getTickets(true);
    }
  }

  concatString(value: any[],key:string): string {
    if (!Array.isArray(value)) return '';
    return value.slice(1).map(scope => scope[key]).join(',');
  }

  /**
   * Set view mode and save to localStorage
   */
  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode = mode;
    localStorage.setItem('tickets-view-mode', mode);
  }

  /**
   * Track by function for ngFor performance optimization
   */
  trackByItemId(index: number, item: any): any {
    return item?.id || index;
  }

  /**
   * Check if any tickets exist
   */
  hasTickets(): boolean {
    return this.ticketList && this.ticketList.length > 0;
  }

  /**
   * Calculate ticket progress based on status - matching Change Order pattern
   */
  calculateTicketProgress(ticket: any): number {
    const name = ticket?.status_label?.name?.toLowerCase() || '';
    if (name.includes('closed') || name.includes('resolved') || name.includes('complete')) return 100;
    if (name.includes('in progress') || name.includes('working')) return 75;
    if (name.includes('assigned') || name.includes('open')) return 50;
    if (name.includes('pending') || name.includes('waiting')) return 25;
    if (name.includes('new') || name.includes('created')) return 10;
    return 0;
  }

  /**
   * Get status class from color for consistency
   */
  getStatusClassFromColor(color: string | null): string {
    if (!color) return 'status-default';
    
    // Map colors to our CSS classes
    const colorLower = color.toLowerCase();
    if (colorLower.includes('009e2a') || colorLower.includes('green')) return 'status-approved';
    if (colorLower.includes('ff9800') || colorLower.includes('orange')) return 'status-pending';
    if (colorLower.includes('f44336') || colorLower.includes('red')) return 'status-rejected';
    if (colorLower.includes('2196f3') || colorLower.includes('blue')) return 'status-in-progress';
    if (colorLower.includes('9e9e9e') || colorLower.includes('gray')) return 'status-draft';
    
    return 'status-default';
  }

  /**
   * Get priority level from label
   */
  getPriorityLevel(priorityLabel: string): 'high' | 'medium' | 'low' {
    const priority = priorityLabel?.toLowerCase() || '';
    if (priority.includes('high') || priority.includes('urgent') || priority.includes('critical')) return 'high';
    if (priority.includes('medium') || priority.includes('normal')) return 'medium';
    return 'low';
  }

  /**
   * Calculate days from creation date
   */
  calculateDaysFromCreation(createdOn: string | Date | null): number {
    if (!createdOn) return 0;
    const created = new Date(createdOn);
    if (isNaN(created.getTime())) return 0;
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Calculate days in current status
   */
  calculateDaysInStatus(updatedOn: string): number {
    if (!updatedOn) return 0;
    const updated = new Date(updatedOn);
    const now = new Date();
    return Math.floor((now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if ticket is overdue
   */
  checkIfOverdue(dueDate: string | null): boolean {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const now = new Date();
    return due < now;
  }

  /**
   * Get display text for scopes
   */
  getScopeDisplayText(scopes: any[]): string {
    if (!scopes || scopes.length === 0) return 'No scopes';
    if (scopes.length === 1) return scopes[0].name;
    return `${scopes[0].name} +${scopes.length - 1} more`;
  }

  /**
   * Get display text for assignees
   */
  getAssigneeDisplayText(assignees: any[]): string {
    if (!assignees || assignees.length === 0) return 'Unassigned';
    // Handle both full_name and name properties for responsible persons
    const firstName = assignees[0]?.full_name || assignees[0]?.name || 'Unknown';
    if (assignees.length === 1) return firstName;
    return `${firstName} +${assignees.length - 1} more`;
  }

  /**
   * Calculate days ago from date string
   */
  calculateDaysAgo(date: string): string {
    if (!date) return '';
    
    const created = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) === 1 ? '' : 's'} ago`;
    
    return created.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: created.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }

  /**
   * Format date to short format (e.g., "Dec 15")
   */
  formatShortDate(dateStr: string | Date | null): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'N/A';
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  }

  /**
   * Check if ticket requires attention
   */
  checkRequiresAttention(ticket: any): boolean {
    // High priority
    if (ticket.priority_label?.toLowerCase().includes('high')) return true;
    // Overdue
    if (this.checkIfOverdue(ticket.due_date)) return true;
    // Old ticket not resolved
    const age = this.calculateDaysFromCreation(ticket.created_on);
    if (age > 14 && !ticket.status_label?.name?.toLowerCase().includes('closed')) return true;
    
    return false;
  }

  /**
   * Get status icon
   */
  getStatusIcon(status: any): string {
    const name = status?.name?.toLowerCase() || '';
    if (name.includes('closed') || name.includes('resolved')) return 'check_circle';
    if (name.includes('pending') || name.includes('waiting')) return 'schedule';
    if (name.includes('rejected') || name.includes('cancelled')) return 'cancel';
    if (name.includes('in progress')) return 'autorenew';
    if (name.includes('new')) return 'fiber_new';
    return 'help_outline';
  }

  /**
   * Get priority icon
   */
  getPriorityIcon(priorityLevel: string): string {
    switch (priorityLevel) {
      case 'high': return 'priority_high';
      case 'medium': return 'remove';
      case 'low': return 'arrow_downward';
      default: return 'remove';
    }
  }

  /**
   * Toggle Info Section visibility
   */
  toggleInfoSection(ticket: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    ticket.showInfo = !ticket.showInfo;
  }

  /**
   * Toggle Details Section visibility
   */
  toggleDetailsSection(ticket: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    ticket.showDetails = !ticket.showDetails;
  }

  /**
   * Toggle Activity Section visibility
   */
  toggleActivitySection(ticket: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    ticket.showActivity = !ticket.showActivity;
  }

  /**
   * Get truncated description for display
   */
  getTruncatedDescription(description: string | null): string {
    if (!description || !description.trim()) return '';
    const maxLength = 100;
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + '...';
  }

  generatePDF(id:any){
    const dialogRef = this.dialog.open(ExportPdfSharedComponent, {
      width: '30%',
      // height: '80%',
      data: {
        moduleName:'ticket',
        recordId: id,
        moduleId: 8,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.helperService.getTemplates(environment.apiBaseUrl+'/ticket/generate-pdf',id,result);
        // this.toasterService.showWarning('PDF export not available.');
      }
    });
  }

  onSearch(){
      this.searchInput.next(this.search);
  }

  /**
   * ===========================
   * TAG PROCESSING METHODS
   * ===========================
   */

  /**
   * Process and normalize tags from API response
   * Sorts by created_at descending (last applied tag first)
   */
  private processTags(tags: any[]): TicketTag[] {
    if (!tags || !Array.isArray(tags)) return [];

    // Map tags and include timestamp fields
    const mappedTags = tags.map(tag => ({
      id: tag.id || tag.tag_id,
      tag_id: tag.tag_id || tag.id,
      name: tag.name || tag.tag || tag.tag_name || '',
      tag: tag.tag || tag.name || tag.tag_name || '',
      color: tag.color || tag.tag_color || 'gray',
      tag_color: tag.tag_color || tag.color || 'gray',
      created_at: tag.created_at,
      updated_at: tag.updated_at
    }));

    // Sort by created_at descending (last applied tag first)
    return mappedTags.sort((a, b) => {
      if (!a.created_at || !b.created_at) return 0;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }

  /**
   * Get tag background color with proper opacity (15% for subtle background)
   */
  getTagBackgroundColor(color: string): string {
    if (!color) return 'rgba(107, 114, 128, 0.15)'; // Default gray

    if (this.isHexColor(color)) {
      return this.hexToRgba(color, 0.15);
    }

    // Use predefined colors from ProSuite tag system
    const colorMap: { [key: string]: string } = {
      blue: 'rgba(59, 130, 246, 0.15)',
      green: 'rgba(34, 197, 94, 0.15)',
      yellow: 'rgba(234, 179, 8, 0.15)',
      red: 'rgba(239, 68, 68, 0.15)',
      purple: 'rgba(168, 85, 247, 0.15)',
      orange: 'rgba(249, 115, 22, 0.15)',
      gray: 'rgba(107, 114, 128, 0.15)',
      pink: 'rgba(236, 72, 153, 0.15)',
      indigo: 'rgba(99, 102, 241, 0.15)',
      teal: 'rgba(20, 184, 166, 0.15)',
    };

    return colorMap[color.toLowerCase()] || colorMap.gray;
  }

  /**
   * Get tag text color (full color for text)
   */
  getTagTextColor(color: string): string {
    if (!color) return '#6b7280'; // Default gray

    if (this.isHexColor(color)) {
      return color;
    }

    // Use predefined colors from ProSuite tag system
    const colorMap: { [key: string]: string } = {
      blue: '#3b82f6',
      green: '#22c55e',
      yellow: '#eab308',
      red: '#ef4444',
      purple: '#a855f7',
      orange: '#f97316',
      gray: '#6b7280',
      pink: '#ec4899',
      indigo: '#6366f1',
      teal: '#14b8a6',
    };

    return colorMap[color.toLowerCase()] || colorMap.gray;
  }

  /**
   * Check if color value is in hex format
   */
  private isHexColor(value: string): boolean {
    if (!value) return false;
    return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(value.trim());
  }

  /**
   * Convert hex color to rgba with specified alpha/opacity
   */
  private hexToRgba(hex: string, alpha: number): string {
    if (!hex) return `rgba(107, 114, 128, ${alpha})`;

    const clean = hex.replace('#', '');

    // Expand 3-digit hex to 6-digit (#abc -> #aabbcc)
    const expand = clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean;

    const r = parseInt(expand.substring(0, 2), 16);
    const g = parseInt(expand.substring(2, 4), 16);
    const b = parseInt(expand.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Get tooltip text for extra tags (when showing +N more)
   */
  getExtraTagsTooltip(tags: TicketTag[], limit: number = 3): string {
    if (!tags || tags.length <= limit) return '';

    const extraTags = tags.slice(limit);
    return extraTags
      .map(tag => tag.name || tag.tag || 'Unknown')
      .join(', ');
  }

  /**
   * Get progress color class based on percentage
   * Matching task-card pattern for consistent visual styling
   */
  getProgressColorClass(progress: number): string {
    if (progress >= 100) return 'progress-complete';
    if (progress >= 75) return 'progress-high';
    if (progress >= 40) return 'progress-medium';
    if (progress > 0) return 'progress-low';
    return 'progress-none';
  }

  ngOnDestroy() {
    this.scrumDataSubscription.unsubscribe();
  }
}
